/**
 * Something the Assistant proposes the analyst records in their own name — an
 * edit to the document's text (shown as a word diff), an answer to an ask,
 * a search they ran, a note, a checklist change — and, at setup, the
 * subjects, the report type and the price. Nothing is recorded until they
 * apply it; applied, it is their own action.
 */

import { useState } from 'react';

import { Badge, Button, Card, Checkbox, Group, Stack, Text } from '@mantine/core';

import type { CopilotProposal } from '@/types/copilot';
import { wordDiff } from './wordDiff';
import classes from './Copilot.module.css';

const KIND: Record<string, string> = {
  edit: 'Edit',
  answer: 'Answer',
  search: 'Search recorded',
  note: 'Note',
  checklist: 'Checklist',
  subjects: 'Subjects',
  report_type: 'Report type',
  price: 'Price',
};

const STATUS: Record<CopilotProposal['status'], { label: string; color: string }> = {
  proposed: { label: 'Proposed', color: 'violet' },
  applied: { label: 'Applied', color: 'teal' },
  dismissed: { label: 'Dismissed', color: 'gray' },
  stale: { label: 'Out of date', color: 'orange' },
};

type Raw = Record<string, unknown>;
const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

function Diff({ before, after }: Readonly<{ before: string; after: string }>) {
  return (
    <Text size="xs" component="div" data-testid="copilot-diff">
      {wordDiff(before, after).map((part, index) =>
        part.kind === 'same' ? (
          <span key={index}>{part.text}</span>
        ) : (
          <span key={index} className={part.kind === 'removed' ? classes.removed : classes.added}>
            {part.text}
          </span>
        )
      )}
    </Text>
  );
}

function Details({ proposal }: Readonly<{ proposal: CopilotProposal }>) {
  const { target, payload } = proposal;
  switch (proposal.kind) {
    case 'edit': {
      const edits = (payload.edits as Raw[] | undefined) ?? [];
      return (
        <Stack gap={6}>
          <Text size="xs" c="dimmed">
            {target.document === 'draft' ? 'The live draft' : 'The report'}
            {target.filename ? ` · ${text(target.filename)}` : ''}
          </Text>
          {edits.map((edit, index) => (
            <Diff key={index} before={text(edit.expected)} after={text(edit.text)} />
          ))}
        </Stack>
      );
    }
    case 'search': {
      const search = (payload.search as Raw | undefined) ?? {};
      const list = (value: unknown) => (Array.isArray(value) ? value.map(text).join(', ') : '');
      return (
        <Stack gap={2}>
          <Text size="xs">
            {list(search.sources)} for {list(search.names)}:{' '}
            {search.result === 'none_found' ? 'nothing found' : text(search.details) || 'found'}
          </Text>
          {search.searched_on ? (
            <Text size="xs" c="dimmed">
              Searched {text(search.searched_on)}
            </Text>
          ) : null}
          {search.limits ? (
            <Text size="xs" c="dimmed">
              Limits: {text(search.limits)}
            </Text>
          ) : null}
        </Stack>
      );
    }
    case 'note':
      return <Text size="xs">{text(payload.statement)}</Text>;
    case 'checklist': {
      const action = text(payload.action);
      return (
        <Text size="xs">
          {action === 'add'
            ? `Add: ${text(payload.title)}`
            : action === 'remove'
              ? `Remove: ${text(target.title ?? payload.title)}`
              : `${text(target.title ?? payload.title)} → ${text(payload.status).replace(/_/g, ' ')}`}
          {payload.note ? ` — ${text(payload.note)}` : ''}
          {payload.reason ? ` — ${text(payload.reason)}` : ''}
        </Text>
      );
    }
    case 'subjects': {
      const subjects = (payload.subjects as Raw[] | undefined) ?? [];
      return (
        <Stack gap={2}>
          {subjects.map((subject, index) => (
            <Text key={index} size="xs">
              {text(subject.name)}
              {subject.subject_type ? ` · ${text(subject.subject_type)}` : ''}
              {Array.isArray(subject.jurisdictions) && subject.jurisdictions.length
                ? ` · ${subject.jurisdictions.map(text).join(', ')}`
                : ''}
              {subject.role ? ` · ${text(subject.role)}` : ''}
              {subject.where ? (
                <Text span size="xs" c="dimmed">
                  {' '}
                  (from {text(subject.where)})
                </Text>
              ) : null}
            </Text>
          ))}
        </Stack>
      );
    }
    case 'report_type':
      return (
        <Text size="xs">
          <strong>{text(payload.code)}</strong>
          {payload.reason ? ` — ${text(payload.reason)}` : ''}
        </Text>
      );
    case 'price':
      return (
        <Text size="xs">
          <strong>
            {text(payload.amount)} {text(payload.currency)}
          </strong>
          {payload.turnaround_days !== undefined && payload.turnaround_days !== null
            ? ` · ${text(payload.turnaround_days)} days`
            : ''}
          {payload.reason ? ` — ${text(payload.reason)}` : ''}
        </Text>
      );
    case 'answer': {
      const answer = (payload.answer as Raw | undefined) ?? {};
      const shown =
        answer.approved !== undefined
          ? answer.approved
            ? 'Approve'
            : 'Decline'
          : text(
              (answer.identity as Raw | undefined)?.decision ??
                answer.choice ??
                answer.value ??
                answer.text
            );
      return (
        <Text size="xs">
          {text(target.question)} → <strong>{shown}</strong>
          {answer.note ? ` — ${text(answer.note)}` : ''}
        </Text>
      );
    }
    default:
      return null;
  }
}

interface ProposalCardProps {
  proposal: CopilotProposal | null;
  /** While the proposal itself hasn't arrived: what the reply says of it. */
  summary: string;
  kind: string;
  people: Record<string, string>;
  onApply: (id: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  /** One of several the reply proposed: chosen for applying together. */
  chosen?: boolean;
  onChoose?: (id: string, chosen: boolean) => void;
}

export function ProposalCard({
  proposal,
  summary,
  kind,
  people,
  onApply,
  onDismiss,
  chosen,
  onChoose,
}: Readonly<ProposalCardProps>) {
  const [busy, setBusy] = useState<'apply' | 'dismiss' | null>(null);
  const status = STATUS[proposal?.status ?? 'proposed'];
  const act = async (how: 'apply' | 'dismiss') => {
    if (!proposal) {
      return;
    }
    setBusy(how);
    try {
      await (how === 'apply' ? onApply(proposal.id) : onDismiss(proposal.id));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card withBorder padding="xs" radius="sm" data-testid="copilot-proposal">
      <Stack gap={6}>
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            {onChoose && proposal?.status === 'proposed' ? (
              <Checkbox
                size="xs"
                checked={chosen ?? false}
                onChange={(e) => onChoose(proposal.id, e.currentTarget.checked)}
                aria-label="Choose this proposal"
                data-testid="copilot-proposal-choose"
              />
            ) : null}
            <Text size="xs" fw={600}>
              {KIND[kind] ?? kind}: {proposal?.summary ?? summary}
            </Text>
          </Group>
          <Badge size="xs" variant="light" color={status.color}>
            {status.label}
          </Badge>
        </Group>
        {proposal ? <Details proposal={proposal} /> : null}
        {proposal?.status === 'proposed' ? (
          <Group gap="xs" justify="flex-end">
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              loading={busy === 'dismiss'}
              disabled={busy !== null}
              onClick={() => void act('dismiss')}
              data-testid="copilot-proposal-dismiss"
            >
              Dismiss
            </Button>
            <Button
              size="compact-xs"
              loading={busy === 'apply'}
              disabled={busy !== null}
              onClick={() => void act('apply')}
              data-testid="copilot-proposal-apply"
            >
              Apply
            </Button>
          </Group>
        ) : null}
        {proposal && proposal.status !== 'proposed' && proposal.decidedBy ? (
          <Text size="xs" c="dimmed">
            {status.label} by {people[proposal.decidedBy] ?? proposal.decidedBy}
            {proposal.status === 'stale' && proposal.error ? ` — ${proposal.error}` : ''}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}
